import dayjs from "dayjs";
import i18next from "i18next";
import { type SupportedLanguage, toSupportedLanguage } from "./languageGeo";

/**
 * dayjs month/day names and `.fromNow()` ("a month ago") follow the
 * dashboard language. Each locale is its own small chunk, loaded when the
 * language is picked; English is built in.
 */
const LOADERS: Record<
	Exclude<SupportedLanguage, "en">,
	() => Promise<unknown>
> = {
	fr: () => import("dayjs/locale/fr"),
	de: () => import("dayjs/locale/de"),
	it: () => import("dayjs/locale/it"),
	pt: () => import("dayjs/locale/pt"),
	nl: () => import("dayjs/locale/nl"),
	es: () => import("dayjs/locale/es"),
	zh: () => import("dayjs/locale/zh-cn"),
	"zh-Hant": () => import("dayjs/locale/zh-tw"),
	ja: () => import("dayjs/locale/ja"),
	ko: () => import("dayjs/locale/ko"),
	ar: () => import("dayjs/locale/ar"),
	hi: () => import("dayjs/locale/hi"),
	id: () => import("dayjs/locale/id"),
	tr: () => import("dayjs/locale/tr"),
	pl: () => import("dayjs/locale/pl"),
	vi: () => import("dayjs/locale/vi"),
	th: () => import("dayjs/locale/th"),
	ru: () => import("dayjs/locale/ru"),
	uk: () => import("dayjs/locale/uk"),
};

/** dayjs's own name for each language's locale. */
export const DAYJS_LOCALE: Record<SupportedLanguage, string> = {
	en: "en",
	fr: "fr",
	de: "de",
	it: "it",
	pt: "pt",
	nl: "nl",
	es: "es",
	zh: "zh-cn",
	"zh-Hant": "zh-tw",
	ja: "ja",
	ko: "ko",
	ar: "ar-latn",
	hi: "hi",
	id: "id",
	tr: "tr",
	pl: "pl",
	vi: "vi",
	th: "th",
	ru: "ru",
	uk: "uk",
};

let pending = 0;

/** Switch dayjs to `language`, loading its locale first. */
export async function syncDayjsLocale(
	language: SupportedLanguage,
): Promise<void> {
	const ticket = ++pending;
	let loaded: unknown;
	if (language !== "en") {
		try {
			loaded = await LOADERS[language]();
		} catch {
			// A failed chunk keeps the previous locale rather than breaking dates.
			return;
		}
	}
	// A later switch won the race; don't overwrite it.
	if (ticket !== pending) return;
	if (language === "ar") {
		// dayjs's Arabic rewrites digits as ١٦; the dashboard (money, counts,
		// codes) uses Latin digits, so register Arabic without that step.
		const ar = (loaded as { default: ILocale }).default;
		dayjs.locale({
			...ar,
			name: DAYJS_LOCALE.ar,
			preparse: undefined,
			postformat: undefined,
		} as ILocale);
		return;
	}
	dayjs.locale(DAYJS_LOCALE[language]);
}

/**
 * The BCP 47 tag for `Intl` formatters (dates, numbers, plural rules) in the
 * dashboard language. English keeps "en-US", which the app always used.
 */
export function intlLocale(language: SupportedLanguage): string {
	if (language === "en") return "en-US";
	// Latin digits, like the rest of the dashboard (see syncDayjsLocale).
	if (language === "ar") return "ar-u-nu-latn";
	if (language === "zh") return "zh-CN";
	if (language === "zh-Hant") return "zh-TW";
	return language;
}

/** The `Intl` tag for the language the dashboard is showing right now. */
export function currentIntlLocale(): string {
	return intlLocale(toSupportedLanguage(i18next.language) ?? "en");
}

/** A count in the dashboard language: 1,000 / 1.000 / 1 000. */
export function formatCount(
	value: number,
	options?: Intl.NumberFormatOptions,
): string {
	return new Intl.NumberFormat(currentIntlLocale(), options).format(value);
}
